export type MotionTier = 'full' | 'lite' | 'reduce';
export type MotionPerfTier = 'full' | 'lite';

export const MOTION_STORAGE_KEY = 'gas-motion-perf';
export const MOTION_ATTR = 'data-motion';

const FPS_SAMPLE_MS = 1000;
const FPS_LITE_THRESHOLD = 20;
const HARDWARE_CORES_MAX = 4;
const HARDWARE_MEMORY_MAX = 4;

type MotionListener = (tier: MotionTier) => void;

const listeners = new Set<MotionListener>();

let currentTier: MotionTier = 'full';
let fpsStarted = false;
let initialized = false;

export const prefersReducedMotion = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const isLowHardware = (): boolean => {
  const cores = navigator.hardwareConcurrency;
  if (typeof cores === 'number' && cores > 0 && cores <= HARDWARE_CORES_MAX) {
    return true;
  }

  const memory = (navigator as Navigator & { deviceMemory?: number })
    .deviceMemory;
  return typeof memory === 'number' && memory <= HARDWARE_MEMORY_MAX;
};

export const getCachedPerfTier = (): MotionPerfTier | null => {
  try {
    const cached = sessionStorage.getItem(MOTION_STORAGE_KEY);
    if (cached === 'full' || cached === 'lite') {
      return cached;
    }
  } catch {
    // sessionStorage may be unavailable
  }
  return null;
};

export const setCachedPerfTier = (tier: MotionPerfTier): void => {
  try {
    sessionStorage.setItem(MOTION_STORAGE_KEY, tier);
  } catch {
    // sessionStorage may be unavailable
  }
};

export const resolveMotionTier = (
  perfHint: MotionPerfTier | null = getCachedPerfTier(),
): MotionTier => {
  if (prefersReducedMotion()) {
    return 'reduce';
  }
  if (perfHint === 'lite' || isLowHardware()) {
    return 'lite';
  }
  if (perfHint === 'full') {
    return 'full';
  }
  return 'full';
};

export const getMotionTier = (): MotionTier => {
  const attr = document.documentElement.getAttribute(MOTION_ATTR);
  if (attr === 'full' || attr === 'lite' || attr === 'reduce') {
    currentTier = attr;
    return attr;
  }
  return currentTier;
};

export const isMotionFull = (): boolean => getMotionTier() === 'full';
export const isMotionLite = (): boolean => getMotionTier() === 'lite';
export const isMotionReduce = (): boolean => getMotionTier() === 'reduce';

export const applyMotionTier = (tier: MotionTier): void => {
  currentTier = tier;
  document.documentElement.setAttribute(MOTION_ATTR, tier);
  listeners.forEach((listener) => {
    listener(tier);
  });
};

export const subscribeMotion = (listener: MotionListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const runFpsProbe = (): void => {
  if (fpsStarted || prefersReducedMotion() || isLowHardware()) {
    return;
  }

  const cached = getCachedPerfTier();
  if (cached !== null) {
    return;
  }

  fpsStarted = true;
  let frames = 0;
  let start: number | null = null;

  const tick = (time: number): void => {
    if (start === null) {
      start = time;
    }
    frames += 1;

    if (time - start < FPS_SAMPLE_MS) {
      requestAnimationFrame(tick);
      return;
    }

    const fps = (frames * 1000) / (time - start);
    const perfTier: MotionPerfTier = fps < FPS_LITE_THRESHOLD ? 'lite' : 'full';
    setCachedPerfTier(perfTier);

    if (prefersReducedMotion()) {
      applyMotionTier('reduce');
      return;
    }

    applyMotionTier(perfTier);
  };

  requestAnimationFrame(tick);
};

const syncFromEnvironment = (): void => {
  const cached = getCachedPerfTier();
  const next = resolveMotionTier(cached);
  applyMotionTier(next);

  if (prefersReducedMotion()) {
    return;
  }

  if (next === 'lite' && cached === null) {
    setCachedPerfTier('lite');
    return;
  }

  if (next === 'full' && cached === null) {
    runFpsProbe();
  }
};

/**
 * Early sync path: set data-motion before Alpine / paint-sensitive work.
 * Safe to call multiple times.
 */
export const initMotion = (): void => {
  if (initialized) {
    syncFromEnvironment();
    return;
  }
  initialized = true;

  syncFromEnvironment();

  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const onReduceChange = (): void => {
    syncFromEnvironment();
  };

  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', onReduceChange);
  } else {
    media.addListener(onReduceChange);
  }
};
